import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useContactPersonCreateMutation, useContactPersonUpdateMutation, useLanguageQuery } from 'api';
import { Form, notification } from 'components/index';
import {
  resetContactInfoFields,
  resetEditId,
  resetIsChanged,
  resetOptionType,
} from 'components/common/ContactFieldManager/contactFieldManagerSlice';
import { ContactInfoGrouped } from 'feature/clientTabs/general/contactInfo/useContactInfo';
import { useAppDispatch } from 'store/hooks';
import { useTypedSelector } from 'store/store';
import { ErrorMappingValueType } from 'typings';
import { generateContactInfoList, groupContactInfoByType } from 'utils/contactUtils';
import { IAddContactPerson } from 'components/common/contactPerson/addEdit';

export const useAddEditContactPerson = (props: IAddContactPerson) => {
  const { modalState, entityName, onModalStateChange, dataToEdit, clientId } = props;
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const contacts = useTypedSelector((state) => state.contactFieldManager.contactInfoFields);
  const isChanged = useTypedSelector((state) => state.contactFieldManager.isChanged);
  const [form] = Form.useForm();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { data: languageData = [] } = useLanguageQuery();
  const [clientContactPersonCreate, { isLoading: createIsLoading }] = useContactPersonCreateMutation();
  const [clientContactPersonUpdate, { isLoading: updateIsLoading }] = useContactPersonUpdateMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );
  const initialContactPerson: Record<string, string | undefined> = useMemo(
    () => ({
      fullName: dataToEdit?.fullName,
      languageId: dataToEdit?.language?.id,
      role: dataToEdit?.role,
      department: dataToEdit?.department,
    }),
    [dataToEdit]
  );
  const clientContactPersonContactList = useMemo(
    () => generateContactInfoList(dataToEdit?.contacts),
    [dataToEdit?.contacts]
  );
  const contactInfoToUpdate: ContactInfoGrouped = useMemo(() => groupContactInfoByType(contacts), [contacts]);
  const onValuesChange = useCallback(() => setDisableSaveBtn(false), []);
  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
    setDisableSaveBtn(true);
    dispatch(resetEditId());
    dispatch(resetIsChanged());
    dispatch(resetOptionType());
    dispatch(resetContactInfoFields());
  }, [form, onModalStateChange, dispatch]);

  const onContactPersonSubmit = useCallback(async () => {
    let contactData = contactInfoToUpdate;
    try {
      const fieldsToValidate = ['languageId', 'fullName'];
      if (!contacts.length) {
        fieldsToValidate.push('type', 'value');
      }
      await form.validateFields(fieldsToValidate);
      if (!contacts.length) {
        contactData = groupContactInfoByType([form.getFieldsValue(['type', 'value'])]);
      }
    } catch {
      return;
    }
    const payload = form.getFieldsValue() || {};
    payload.contacts = contactData;
    payload.entityId = (clientId || id) as string;
    payload.entity = entityName;

    if (dataToEdit) {
      payload.id = dataToEdit.id;
    }
    (dataToEdit ? clientContactPersonUpdate : clientContactPersonCreate)(payload)
      .then(({ data, error }: any) => {
        if (data?.success) {
          openNotificationWithIcon({
            type: 'success',
            message: `The contact person was successfully ${dataToEdit ? 'updated' : 'created'}.`,
          });
          onCancel();
        } else if (!error.data.success) {
          const { data = {} } = error.data;
          Object.keys(data).forEach((_key: string) => {
            openNotificationWithIcon({
              type: 'error',
              message: data[_key],
            });
          });
        } else if (error.data.error) {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      })
      .catch((err) => {
        console.log(err, 'error');
      });
  }, [
    id,
    form,
    clientContactPersonCreate,
    clientContactPersonUpdate,
    onCancel,
    openNotificationWithIcon,
    dataToEdit,
    contacts.length,
    entityName,
    contactInfoToUpdate,
    clientId,
  ]);

  useEffect(() => {
    if (modalState && initialContactPerson) {
      form.setFieldsValue(initialContactPerson);
    }
  }, [form, initialContactPerson, modalState]);

  useEffect(() => {
    if (modalState && isChanged) {
      setDisableSaveBtn(false);
    }
  }, [isChanged, modalState]);
  return {
    form,
    contextHolder,
    disableSaveBtn,
    languageData,
    clientContactPersonContactList,
    onContactPersonSubmit,
    onCancel,
    onValuesChange,
    isLoading: createIsLoading || updateIsLoading,
  };
};
